import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Clock, MapPin, Car, User, Wrench, Hammer, Settings, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { getCalendarBookings } from '../lib/bookings';

interface DaySummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  isLocationBooking: boolean;
  onCreateBooking: (date: Date) => void;
  onBookingClick: (bookingId: string) => void;
  salesSumEnabled?: boolean;
  bookingClickEnabled?: boolean;
}

interface Booking {
  id: string;
  start: string;
  end: string;
  title: string;
  backgroundColor: string;
  secondaryColor?: string | null;
  extendedProps: {
    customer: string;
    booking_number: number;
    status: string;
    total_amount: number;
    resources?: Array<{
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
      role?: string;
      type?: string;
    }>;
  };
}

// Get color based on status
const getStatusColor = (status: string) => {
  // For in_progress, always use yellow
  if (status === 'in_progress') {
    return '#eab308'; // Yellow
  }
  
  // Use default status colors for other statuses
  switch (status) {
    case 'pending':
      return '#a855f7'; // Purple
    case 'scheduled':
      return '#3b82f6'; // Blue (default)
    case 'completed':
      return '#22c55e'; // Green
    default:
      return '#3b82f6'; // Default blue
  }
};

// Helper function to get the appropriate icon for a resource
const getResourceIcon = (resource: { role?: string; type?: string }) => {
  const iconClass = "h-4 w-4 text-white";
  switch (resource.role?.toLowerCase() || resource.type?.toLowerCase()) {
    case 'technician':
      return <Wrench className={iconClass} />;
    case 'mechanic':
      return <Hammer className={iconClass} />;
    case 'service':
      return <Settings className={iconClass} />;
    case 'vehicle':
      return <Car className={iconClass} />;
    default:
      return <User className={iconClass} />;
  }
};

export default function DaySummaryModal({ isOpen, onClose, selectedDate, position, isLocationBooking, onCreateBooking, onBookingClick, salesSumEnabled = true, bookingClickEnabled = true }: DaySummaryModalProps) {
  const { selectedBusiness } = useBusiness();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle clicking outside to close
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      // Add both mouse and touch event listeners
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      // Clean up both listeners
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Prevent body scroll when modal is open on mobile
    if (isOpen && typeof window !== 'undefined' && window.innerWidth < 640) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    async function fetchBookings() {
      if (!selectedDate || !selectedBusiness?.id || !isOpen) return;

      setLoading(true);
      setError(null);

      try {
        // Create start and end of the selected day in local timezone
        const startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);

        const dayBookings = await getCalendarBookings(
          selectedBusiness.id,
          startDate,
          endDate,
          isLocationBooking
        );

        // Filter bookings that overlap with the selected date
        const filteredBookings = dayBookings.filter(booking => {
          const bookingStart = new Date(booking.start);
          const bookingEnd = new Date(booking.end);
          
          // A booking overlaps if:
          // 1. It starts before the end of the day AND
          // 2. It ends after the start of the day
          return bookingStart <= endDate && bookingEnd >= startDate;
        });

        // Sort bookings by start time
        const sortedBookings = filteredBookings.sort((a, b) => 
          new Date(a.start).getTime() - new Date(b.start).getTime()
        );

        setBookings(sortedBookings);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    }

    fetchBookings();
  }, [selectedDate, selectedBusiness?.id, isOpen, isLocationBooking]);

  if (!isOpen) return null;

  // Calculate optimal position
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const isMobile = screenWidth < 640; // sm breakpoint
  const dropdownWidth = isMobile ? screenWidth - 32 : 512; // Full width - padding on mobile
  const dropdownHeight = 400; // Approximate max height of the dropdown
  const arrowSize = 8; // Size of the arrow
  const padding = 16; // Padding from viewport edges
  const modalGap = 12; // Gap between modal content and arrow

  // Only calculate complex positioning for desktop
  if (!isMobile) {
    // Determine if modal should appear above or below
    const spaceBelow = screenHeight - position.y;
    const spaceAbove = position.y;
    const isUpward = spaceBelow < (dropdownHeight + modalGap) && spaceAbove > spaceBelow;

    // Center the modal horizontally relative to the cell
    let left = position.x + (position.width / 2) - (dropdownWidth / 2);
    
    // Ensure modal stays within screen bounds horizontally
    if (left + dropdownWidth > screenWidth - padding) {
      left = screenWidth - dropdownWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }

    return (
      <div
        ref={dropdownRef}
        className="fixed z-50"
        style={{
          left: left,
          width: dropdownWidth,
          top: isUpward ? undefined : position.y + modalGap,
          bottom: isUpward ? (window.innerHeight - position.y + modalGap) : undefined,
        }}
      >
        {/* Arrow */}
        <div
          className={`absolute left-1/2 w-0 h-0 border-l-8 border-r-8 border-solid border-l-transparent border-r-transparent ${
            isUpward
              ? 'bottom-0 border-b-8 border-b-white translate-y-full -translate-x-1/2'
              : 'top-0 border-t-8 border-t-white -translate-y-full -translate-x-1/2'
          }`}
        />
        
        {/* Main modal content */}
        <div 
          className="bg-white rounded-lg shadow-xl border border-gray-200 relative w-[32rem]"
          style={{
            maxHeight: `${dropdownHeight}px`
          }}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="text-lg font-medium">
              {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Day Summary'}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => selectedDate && onCreateBooking(selectedDate)}
                className="text-gray-900 hover:text-gray-700 p-1"
                aria-label="New Booking"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 p-2 -mr-2"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div 
            className="p-4 overflow-y-auto"
            style={{ 
              maxHeight: `calc(100vh - ${padding * 4}px - 3.5rem)`
            }}
          >
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : error ? (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-8">
                No bookings scheduled for this day
              </div>
            ) : (
              <div className="space-y-2">
                {bookings.map((booking) => (
                  <div 
                    key={booking.id}
                    className={`border rounded-lg h-20 hover:bg-opacity-90 relative overflow-hidden ${bookingClickEnabled ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={() => bookingClickEnabled && onBookingClick(booking.id)}
                    style={{
                      backgroundColor: booking.extendedProps.status === 'in_progress' 
                        ? '#eab308' // Yellow for in_progress
                        : booking.backgroundColor,
                      background: booking.extendedProps.status === 'in_progress'
                        ? '#eab308' // Yellow for in_progress
                        : booking.secondaryColor 
                          ? `linear-gradient(135deg, ${booking.backgroundColor} 60%, ${booking.secondaryColor} 60%)`
                          : booking.backgroundColor,
                      borderColor: 'transparent'
                    }}
                  >
                    <div className="p-2 h-full flex flex-col justify-between">
                      {/* Top section with booking info */}
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium text-white">
                            #{booking.extendedProps.booking_number} - {booking.title}
                          </div>
                          <div className="text-xs text-white/80">
                            {booking.extendedProps.customer}
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize bg-white/20 text-white">
                            {booking.extendedProps.status}
                          </span>
                          {salesSumEnabled && (
                            <span className="text-xs font-medium text-white mt-0.5">
                              ${booking.extendedProps.total_amount.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bottom section with resources */}
                      <div className="flex items-center gap-1.5">
                        {booking.extendedProps.resources?.map((resource) => {
                          // Get initials from first and last name
                          const initials = `${resource.first_name[0]}${resource.last_name[0]}`.toUpperCase();
                          
                          return (
                            <div 
                              key={resource.id} 
                              className="flex items-center justify-center w-6 h-6 rounded-full bg-black/20"
                              title={`${resource.first_name} ${resource.last_name}`}
                            >
                              {resource.avatar_url ? (
                                <img 
                                  src={resource.avatar_url} 
                                  alt={`${resource.first_name} ${resource.last_name}`}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-xs font-medium text-white">
                                  {initials}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout - centered in the screen
  return (
    <>
      {/* Mobile overlay backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        onTouchEnd={onClose}
      />
      <div
        ref={dropdownRef}
        className="fixed z-50 inset-x-4 top-1/2 -translate-y-1/2"
      >
        {/* Main modal content */}
        <div 
          className="bg-white rounded-lg shadow-xl border border-gray-200 relative w-full"
          style={{
            maxHeight: `calc(100vh - ${padding * 4}px)`
          }}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="text-lg font-medium">
              {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Day Summary'}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => selectedDate && onCreateBooking(selectedDate)}
                className="text-gray-900 hover:text-gray-700 p-1"
                aria-label="New Booking"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 p-2 -mr-2"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div 
            className="p-4 overflow-y-auto"
            style={{ 
              maxHeight: `calc(100vh - ${padding * 4}px - 3.5rem)`
            }}
          >
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : error ? (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-8">
                No bookings scheduled for this day
              </div>
            ) : (
              <div className="space-y-2">
                {bookings.map((booking) => (
                  <div 
                    key={booking.id}
                    className={`border rounded-lg h-20 hover:bg-opacity-90 relative overflow-hidden ${bookingClickEnabled ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={() => bookingClickEnabled && onBookingClick(booking.id)}
                    style={{
                      backgroundColor: booking.extendedProps.status === 'in_progress' 
                        ? '#eab308' // Yellow for in_progress
                        : booking.backgroundColor,
                      background: booking.extendedProps.status === 'in_progress'
                        ? '#eab308' // Yellow for in_progress
                        : booking.secondaryColor 
                          ? `linear-gradient(135deg, ${booking.backgroundColor} 60%, ${booking.secondaryColor} 60%)`
                          : booking.backgroundColor,
                      borderColor: 'transparent'
                    }}
                  >
                    <div className="p-2 h-full flex flex-col justify-between">
                      {/* Top section with booking info */}
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium text-white">
                            #{booking.extendedProps.booking_number} - {booking.title}
                          </div>
                          <div className="text-xs text-white/80">
                            {booking.extendedProps.customer}
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize bg-white/20 text-white">
                            {booking.extendedProps.status}
                          </span>
                          {salesSumEnabled && (
                            <span className="text-xs font-medium text-white mt-0.5">
                              ${booking.extendedProps.total_amount.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bottom section with resources */}
                      <div className="flex items-center gap-1.5">
                        {booking.extendedProps.resources?.map((resource) => {
                          // Get initials from first and last name
                          const initials = `${resource.first_name[0]}${resource.last_name[0]}`.toUpperCase();
                          
                          return (
                            <div 
                              key={resource.id} 
                              className="flex items-center justify-center w-6 h-6 rounded-full bg-black/20"
                              title={`${resource.first_name} ${resource.last_name}`}
                            >
                              {resource.avatar_url ? (
                                <img 
                                  src={resource.avatar_url} 
                                  alt={`${resource.first_name} ${resource.last_name}`}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-xs font-medium text-white">
                                  {initials}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
} 